<template>
  <div class="container">
    <div class="gift-card-lookup__inner">
      <form
        class="gift-card-lookup__form"
        novalidate
        @submit.prevent="lookup"
      >
        <p
          v-if="data.helper_text"
          :id="helperId"
          class="gift-card-lookup__helper"
        >
          {{ data.helper_text }}
        </p>
        <div
          class="gift-card-lookup__field input-placeholder"
          :class="{ 'has-value': code }"
        >
          <input
            :id="inputId"
            v-model="code"
            class="input"
            type="text"
            maxlength="40"
            autocomplete="off"
            autocapitalize="characters"
            autocorrect="off"
            spellcheck="false"
            :aria-describedby="describedBy"
            :aria-invalid="isInputError ? 'true' : undefined"
            @input="reset"
          />
          <label :for="inputId">{{ data.input_label }}</label>
        </div>
        <button
          class="gift-card-lookup__btn btn btn--primary"
          type="submit"
          :aria-busy="isLoading ? 'true' : 'false'"
        >
          {{ data.button_text }}
        </button>
      </form>
      <div
        :id="resultId"
        class="gift-card-lookup__result"
        aria-live="polite"
      >
        <template v-if="status === 'found'">
          <p class="gift-card-lookup__balance-label">
            {{ data.balance_label }}
          </p>
          <p class="gift-card-lookup__balance">
            {{ balance }}
          </p>
        </template>
        <template v-else-if="status === 'balance_unavailable'">
          <p class="gift-card-lookup__balance-label">
            {{ data.original_value_label }}
          </p>
          <p class="gift-card-lookup__balance">
            {{ balance }}
          </p>
          <p class="gift-card-lookup__note">
            {{ data.balance_unavailable_message }}
          </p>
        </template>
        <p
          v-else-if="message"
          class="gift-card-lookup__message"
          :class="{ 'gift-card-lookup__message--error': isInputError || status === 'error' }"
        >
          {{ message }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const MIN_CODE_LENGTH = 8;
const MAX_CODE_LENGTH = 16;

const code = ref('');
const status = ref('idle');
const balance = ref('');
let requestId = 0;

const inputId = `${props.data.section_id}-code`;
const helperId = `${props.data.section_id}-helper`;
const resultId = `${props.data.section_id}-result`;

const isLoading = computed(() => status.value === 'loading');
const isInputError = computed(() => ['invalid', 'not_found'].includes(status.value));
const describedBy = computed(() => [props.data.helper_text && helperId, resultId].filter(Boolean).join(' '));

const message = computed(() => {
  const messages = {
    invalid: props.data.invalid_message,
    inactive: props.data.inactive_message,
    expired: props.data.expired_message,
    not_found: props.data.not_found_message,
    error: props.data.error_message,
  };

  return messages[status.value] || '';
});

function normalizeCode(value) {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// expiresOn is a YYYY-MM-DD string; new Date() would parse it as UTC and shift it a day in US time zones
function localDateString() {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${today.getFullYear()}-${month}-${day}`;
}

function resolveStatus(giftCard) {
  if (giftCard.enabled === false || giftCard.deactivatedAt) return 'inactive';
  if (giftCard.expiresOn && giftCard.expiresOn < localDateString()) return 'expired';
  if (giftCard.liveBalance === false) return 'balance_unavailable';

  return 'found';
}

function reset() {
  requestId += 1;
  status.value = 'idle';
  balance.value = '';
}

async function lookup() {
  if (isLoading.value) return;

  const normalizedCode = normalizeCode(code.value);

  if (normalizedCode.length < MIN_CODE_LENGTH || normalizedCode.length > MAX_CODE_LENGTH) {
    status.value = 'invalid';
    return;
  }

  const host = (SDG.Data.joineryHost || '').replace(/\/+$/, '');

  if (!host) {
    status.value = 'error';
    return;
  }

  requestId += 1;
  const currentRequestId = requestId;
  status.value = 'loading';

  try {
    const response = await fetch(`${host}/api/v1/shopify_gift_cards/${encodeURIComponent(normalizedCode)}`);

    if (!response.ok && response.status !== 404) throw new Error(`Gift card lookup failed with ${response.status}`);

    const body = response.ok ? await response.json() : null;

    if (currentRequestId !== requestId) return;

    const giftCard = body?.giftCard;

    if (!giftCard) {
      status.value = 'not_found';
      return;
    }

    const amount = giftCard.liveBalance === false ? giftCard.initialValue : giftCard.balance;

    balance.value = __.formatPriceDisplay(Math.round(parseFloat(amount) * 100), 'money');
    status.value = resolveStatus(giftCard);
  } catch (error) {
    if (currentRequestId !== requestId) return;

    status.value = 'error';
  }
}
</script>
